// BizPlus parity Phase 1.5 — configurable print templates.
//
// Pick a document type, author its header/footer chrome, toggle its
// presentation flags, and preview the result against a sample record before
// publishing. Flags control what the print SHOWS, never what it computes —
// the totals are identical whichever way they are set.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Printer, Check, Star, Trash2, Eye } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Upload } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { SettingsSubNav } from '../components/SettingsSubNav';
import {
  TemplateDesignEditor,
  type TemplateAsset,
  type TokenSuggestion,
} from '../components/TemplateDesignEditor';

interface TemplateFlags {
  taxColumn: boolean;
  discountedPrice: boolean;
  contactBlock: boolean;
  amountInWords: boolean;
  hideTotalTax: boolean;
  hideAmount: boolean;
}

interface TemplateStyle {
  primaryColor: string;
  accentColor: string;
  textColor: string;
  fontFamily: string;
  baseFontSize: number;
  lineHeight: number;
  pagePadding: number;
  headerSpacing: number;
  footerSpacing: number;
  borderRadius: number;
}

interface DocumentTemplate {
  _id: string;
  documentType: string;
  name: string;
  isDefault: boolean;
  layout: string;
  status: 'draft' | 'published';
  headerHtml: string;
  headerImageUrl: string;
  headerHeight: number;
  headerWidth: number;
  footerHtml: string;
  footerImageUrl: string;
  footerHeight: number;
  footerWidth: number;
  flags: TemplateFlags;
  style: TemplateStyle;
  // Imported full-page design (layout 'imported').
  bodyHtml?: string;
  bodyCss?: string;
  pageWidth?: number;
  pageHeight?: number;
  assets?: TemplateAsset[];
  importedFrom?: { fileName: string; importedAt: string | null; pages: number };
}

interface ImportResult {
  html: string;
  css: string;
  pages: number;
  pageWidth: number;
  pageHeight: number;
  textElements: number;
  graphicElements: number;
  warnings: string[];
  suggestions: TokenSuggestion[];
  sourceName: string;
  /** Set when the import targeted header/footer chrome rather than the page. */
  side?: 'header' | 'footer';
}

const COMPANY_TOKENS = '{{company.name}} · {{company.gstin}} · {{company.address}} · {{company.phone}} · {{company.email}}';
const COLOR_FALLBACKS: Record<'primaryColor' | 'accentColor' | 'textColor', string> = {
  primaryColor: '#0f4c81',
  accentColor: '#e8f1fa',
  textColor: '#1f2937',
};

const FLAG_LABELS: Record<keyof TemplateFlags, string> = {
  taxColumn: 'Show tax columns',
  discountedPrice: 'Show discount column',
  contactBlock: 'Show "Bill To" block',
  amountInWords: 'Show amount in words',
  hideTotalTax: 'Hide total tax rows',
  hideAmount: 'Hide all amounts (delivery copy)',
};

// Only document types with a real renderer today can be previewed; the rest
// are configurable but will print with the built-in design until one exists.
const PREVIEWABLE = new Set(['quotation', 'proforma', 'invoice', 'challan']);

export function DocumentTemplatesPage() {
  const qc = useQueryClient();
  const [docType, setDocType] = useState('invoice');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Held only until the admin acts on them: suggestions are tied to the markup
  // that was just converted, so they are cleared whenever the selection moves.
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const meta = useQuery({
    queryKey: ['document-templates', 'meta'],
    queryFn: async () =>
      (
        await api.get<{ documentTypes: string[]; layouts: string[]; flagDefaults: TemplateFlags; styleDefaults: TemplateStyle }>(
          '/api/documents/templates/meta',
        )
      ).data,
  });

  const list = useQuery({
    queryKey: ['document-templates', docType],
    queryFn: async () =>
      (
        await api.get<{ items: DocumentTemplate[] }>(
          `/api/documents/templates?documentType=${docType}`,
        )
      ).data.items,
  });

  const selected = useMemo(
    () => list.data?.find((t) => t._id === selectedId) || null,
    [list.data, selectedId],
  );

  const refresh = () => qc.invalidateQueries({ queryKey: ['document-templates'] });

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post('/api/documents/templates', {
          documentType: docType,
          name: `${docType} template`,
        })
      ).data,
    onSuccess: (d: any) => {
      refresh();
      setSelectedId(d.item?._id ?? null);
      toast.success('Draft template created.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not create the template.')),
  });

  const save = useMutation({
    mutationFn: async (patch: Partial<DocumentTemplate>) =>
      (await api.patch(`/api/documents/templates/${selectedId}`, patch)).data,
    onSuccess: () => {
      refresh();
      toast.success('Saved.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save.')),
  });

  const publish = useMutation({
    mutationFn: async () => (await api.post(`/api/documents/templates/${selectedId}/publish`)).data,
    onSuccess: () => {
      refresh();
      toast.success('Published.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not publish.')),
  });

  /**
   * Import a PDF as the template's whole design.
   *
   * The earlier version dropped the converted markup into headerHtml, which put
   * a complete invoice above a generated items table — printing the document
   * twice. A converted PDF is the page, so it lands in bodyHtml and switches
   * the layout to 'imported', which is the mode that prints it verbatim.
   */
  const importPdf = useMutation({
    mutationFn: async ({ file, side }: { file: File; side?: 'header' | 'footer' }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('documentType', docType);
      // A header or footer receives flowed paragraphs; the whole-page design
      // receives positioned elements. Sending positioned markup into a header
      // block would overlay it on the document body.
      if (side) form.append('mode', 'flow');
      // Content-Type is left unset so the browser supplies the multipart
      // boundary; naming it here strips the boundary and the upload fails.
      const { data } = await api.post<ImportResult>(
        '/api/documents/templates/import-pdf',
        form,
      );
      return { ...data, side };
    },
    onSuccess: (result) => {
      if (result.side) {
        save.mutate({ [`${result.side}Html`]: result.html } as Partial<DocumentTemplate>);
        toast.success(`${result.sourceName} imported into the ${result.side}.`);
        return;
      }

      save.mutate({
        layout: 'imported',
        bodyHtml: result.html,
        bodyCss: result.css,
        pageWidth: result.pageWidth,
        pageHeight: result.pageHeight,
      } as Partial<DocumentTemplate>);
      setImportResult(result);
      (result.warnings ?? []).forEach((w) => toast(w, { duration: 6000 }));
      toast.success(
        `${result.sourceName}: ${result.pages} page${result.pages === 1 ? '' : 's'}, ` +
          `${result.textElements} text elements imported.`,
      );
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not import this PDF.')),
  });

  const makeDefault = useMutation({
    mutationFn: async () => (await api.post(`/api/documents/templates/${selectedId}/default`)).data,
    onSuccess: () => {
      refresh();
      toast.success('This is now the default for this document type.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not set as default.')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/documents/templates/${id}`)).data,
    onSuccess: () => {
      refresh();
      setSelectedId(null);
      toast.success('Template deleted.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not delete.')),
  });

  // Suggestions describe the markup produced by one specific conversion. Moving
  // to another template would offer to rewrite elements that no longer exist,
  // and every one of them would come back as "no longer present".
  useEffect(() => setImportResult(null), [selectedId, docType]);

  const setFlag = (key: keyof TemplateFlags, value: boolean) =>
    save.mutate({ flags: { ...selected!.flags, [key]: value } as TemplateFlags });

  const setStyle = (key: keyof TemplateStyle, value: string | number) =>
    save.mutate({ style: { ...selected!.style, [key]: value } as TemplateStyle });

  return (
    <div>
      <SettingsSubNav />
      <div className="mx-auto max-w-[1580px] space-y-4 pb-16">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-body-sm font-medium text-on-surface-variant">Document type:</span>
          <select
            className="h-9 rounded border border-outline bg-surface px-3 text-data-md"
            value={docType}
            onChange={(e) => {
              setDocType(e.target.value);
              setSelectedId(null);
            }}
          >
            {(meta.data?.documentTypes ?? []).map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
            <Plus className="mr-1 h-4 w-4" /> New template
          </Button>
          <Button
            size="sm"
            variant="secondary"
            title={
              selected
                ? 'Convert a PDF into this template’s editable design'
                : 'Select or create a template first'
            }
            disabled={!selected || importPdf.isPending}
            onClick={() => pdfInputRef.current?.click()}
          >
            <Upload className="mr-1 h-4 w-4" />
            {importPdf.isPending ? 'Converting…' : 'Import PDF design'}
          </Button>
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) importPdf.mutate({ file });
              // Reset so re-picking the same file fires change again.
              e.target.value = '';
            }}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <Card>
            <CardHeader title={`Templates (${list.data?.length ?? 0})`} />
            <CardBody className="p-0">
              {list.isLoading ? (
                <div className="p-4">
                  <Skeleton className="h-32" />
                </div>
              ) : !list.data?.length ? (
                <p className="text-muted-foreground p-6 text-center text-sm">
                  No template for this document type yet — it prints with the built-in design.
                </p>
              ) : (
                <ul className="divide-y">
                  {list.data.map((t) => (
                    <li key={t._id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(t._id)}
                        className={`hover:bg-muted/30 flex w-full items-center gap-2 px-4 py-3 text-left transition-colors ${
                          t._id === selectedId ? 'bg-primary/5' : ''
                        }`}
                      >
                        <Printer className="h-4 w-4 shrink-0 text-primary" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {t.name || '(unnamed)'}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {t.status}
                            {t.isDefault ? ' · default' : ''} · {t.layout}
                          </span>
                        </span>
                        {t.isDefault && <Star className="h-3.5 w-3.5 shrink-0 text-warning-text" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {!selected ? (
            <Card>
              <CardBody>
                <p className="text-muted-foreground text-sm">
                  Select a template to edit, or create one. Until a published template is made the
                  default for a document type, that document prints with the built-in design.
                </p>
              </CardBody>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardHeader
                  title={selected.name || '(unnamed)'}
                  action={
                    <div className="flex flex-wrap gap-2">
                      {PREVIEWABLE.has(selected.documentType) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Preview this template against a sample record — works on drafts too"
                          onClick={() =>
                            window.open(
                              `/api/documents/preview/${selected.documentType}?template=${selected._id}`,
                              '_blank',
                            )
                          }
                        >
                          <Eye className="mr-1 h-4 w-4" /> Preview
                        </Button>
                      )}
                      {selected.status !== 'published' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => publish.mutate()}
                          disabled={publish.isPending}
                        >
                          <Check className="mr-1 h-4 w-4" /> Publish
                        </Button>
                      )}
                      {selected.status === 'published' && !selected.isDefault && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => makeDefault.mutate()}
                          disabled={makeDefault.isPending}
                        >
                          <Star className="mr-1 h-4 w-4" /> Make default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() =>
                          window.confirm('Delete this template?') && remove.mutate(selected._id)
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  }
                />
                <CardBody className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Template name"
                      defaultValue={selected.name}
                      onBlur={(e) =>
                        e.target.value !== selected.name && save.mutate({ name: e.target.value })
                      }
                    />
                    <label className="block">
                      <span className="mb-1 block text-body-sm font-medium text-on-surface-variant">
                        Layout
                      </span>
                      <select
                        className="h-9 w-full rounded border border-outline bg-surface px-3 text-data-md"
                        value={selected.layout}
                        onChange={(e) => save.mutate({ layout: e.target.value })}
                      >
                        {(meta.data?.layouts ?? []).map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </CardBody>
              </Card>

              {selected.layout === 'imported' && (
                <Card>
                  <CardHeader
                    title="Imported design"
                    subtitle={
                      selected.importedFrom?.fileName
                        ? `From ${selected.importedFrom.fileName}`
                        : 'Converted from a PDF — drag elements, restyle them, add a logo, and link live fields.'
                    }
                  />
                  <CardBody>
                    {!selected.bodyHtml ? (
                      <p className="text-body-sm text-on-surface-variant">
                        This template is set to the imported layout but has no design yet.
                        Use <strong>Import PDF design</strong> above to convert one.
                      </p>
                    ) : (
                      <TemplateDesignEditor
                        // Remounts on template change so the canvas never shows
                        // one template's markup under another's id.
                        key={selected._id}
                        templateId={selected._id}
                        bodyHtml={selected.bodyHtml}
                        bodyCss={selected.bodyCss || ''}
                        assets={selected.assets || []}
                        suggestions={importResult?.suggestions ?? []}
                        onSaved={refresh}
                      />
                    )}
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardHeader title="Company branding & print style" />
                <CardBody className="space-y-4">
                  <p className="text-body-sm text-on-surface-variant">
                    These controls apply only to this document type. Company values stay centrally managed,
                    while the logo, colours, font and spacing can differ for quotations, proformas and invoices.
                  </p>
                  <p className="rounded bg-surface-container-low p-3 font-mono text-xs text-on-surface-variant">
                    Use in header/footer HTML: {`${COMPANY_TOKENS} · {{document.number}} · {{document.date}}`}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {([
                      ['primaryColor', 'Primary colour'],
                      ['accentColor', 'Table accent'],
                      ['textColor', 'Text colour'],
                    ] as [keyof typeof COLOR_FALLBACKS, string][]).map(([key, label]) => (
                      <label key={key} className="block text-body-sm font-medium text-on-surface-variant">
                        {label}
                        <span className="mt-1 flex h-9 items-center gap-2 rounded border border-outline bg-surface px-2">
                          <input
                            aria-label={label}
                            className="h-6 w-7 cursor-pointer border-0 bg-transparent p-0"
                            type="color"
                            defaultValue={selected.style?.[key] || COLOR_FALLBACKS[key]}
                            onBlur={(e) => setStyle(key, e.target.value)}
                          />
                          <span className="font-mono text-xs">{selected.style?.[key] || COLOR_FALLBACKS[key]}</span>
                        </span>
                      </label>
                    ))}
                    <Input label="Font family" defaultValue={selected.style?.fontFamily} onBlur={(e) => setStyle('fontFamily', e.target.value)} />
                    <Input label="Text size (px)" type="number" min="8" max="24" defaultValue={selected.style?.baseFontSize} onBlur={(e) => setStyle('baseFontSize', Number(e.target.value))} />
                    <Input label="Line height" type="number" min="1" max="3" step="0.05" defaultValue={selected.style?.lineHeight} onBlur={(e) => setStyle('lineHeight', Number(e.target.value))} />
                    <Input label="Page padding (px)" type="number" min="0" max="96" defaultValue={selected.style?.pagePadding} onBlur={(e) => setStyle('pagePadding', Number(e.target.value))} />
                    <Input label="Header spacing (px)" type="number" min="0" max="96" defaultValue={selected.style?.headerSpacing} onBlur={(e) => setStyle('headerSpacing', Number(e.target.value))} />
                    <Input label="Footer spacing (px)" type="number" min="0" max="96" defaultValue={selected.style?.footerSpacing} onBlur={(e) => setStyle('footerSpacing', Number(e.target.value))} />
                    <Input label="Border radius (px)" type="number" min="0" max="32" defaultValue={selected.style?.borderRadius} onBlur={(e) => setStyle('borderRadius', Number(e.target.value))} />
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Header & footer" />
                <CardBody className="space-y-4">
                  <p className="text-muted-foreground text-xs">
                    Leave both blank to keep the built-in header/footer. HTML is inserted as
                    authored, so an image letterhead or styled block both work. Import text-based
                    PDFs below; scanned PDFs need OCR first. Sizes of 0 mean "fit content".
                  </p>
                  {(['header', 'footer'] as const).map((side) => (
                    <div key={side} className="space-y-3 rounded-md border p-3">
                      <p className="text-sm font-semibold capitalize">{side}</p>
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-outline px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5">
                        <Upload className="h-3.5 w-3.5" />
                        {importPdf.isPending ? 'Converting PDF?' : 'Import text-based PDF'}
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="sr-only"
                          disabled={importPdf.isPending}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) importPdf.mutate({ file, side });
                            e.currentTarget.value = '';
                          }}
                        />
                        <label className="block"></label>
                        <span className="mb-1 block text-body-sm text-on-surface-variant">
                          {side} HTML
                        </span>
                        <textarea
                          className="min-h-[80px] w-full rounded border border-outline bg-surface p-2 font-mono text-xs"
                          defaultValue={
                            side === 'header' ? selected.headerHtml : selected.footerHtml
                          }
                          onBlur={(e) => save.mutate({ [`${side}Html`]: e.target.value } as any)}
                        />
                      </label>
                      <div className="grid gap-3 md:grid-cols-3">
                        <Input
                          label={side === 'header' ? 'Logo / header image URL' : 'Footer image URL'}
                          defaultValue={
                            side === 'header' ? selected.headerImageUrl : selected.footerImageUrl
                          }
                          onBlur={(e) =>
                            save.mutate({ [`${side}ImageUrl`]: e.target.value } as any)
                          }
                        />
                        <Input
                          label="Height (px)"
                          type="number"
                          defaultValue={
                            side === 'header' ? selected.headerHeight : selected.footerHeight
                          }
                          onBlur={(e) =>
                            save.mutate({ [`${side}Height`]: Number(e.target.value) } as any)
                          }
                        />
                        <Input
                          label="Width (px)"
                          type="number"
                          defaultValue={
                            side === 'header' ? selected.headerWidth : selected.footerWidth
                          }
                          onBlur={(e) =>
                            save.mutate({ [`${side}Width`]: Number(e.target.value) } as any)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="What to show" />
                <CardBody>
                  <p className="text-muted-foreground mb-3 text-xs">
                    These change presentation only. Totals and tax are computed identically either
                    way, so two prints of one document can never disagree about the amount owed.
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(Object.keys(FLAG_LABELS) as (keyof TemplateFlags)[]).map((key) => (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!selected.flags?.[key]}
                          onChange={(e) => setFlag(key, e.target.checked)}
                        />
                        <span>{FLAG_LABELS[key]}</span>
                      </label>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
